// filepath: src/services/axiosInstance.js

import axios from "axios";
import { emitNetworkStatus, NETWORK_STATUS } from "../utils/networkStatusEvents";
import { resolveApiUrl } from "../utils/apiBaseUrl";
import { emitSessionExpired, isLoginRoute } from "../utils/sessionExpiredEvents";

// Create a single reusable axios instance
const axiosInstance = axios.create({
    baseURL: resolveApiUrl("/api"), // backend API root
});

export const getTenantSubdomain = () => {
    const hostname = window.location.hostname.toLowerCase();
    if (!hostname || hostname === "localhost" || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return "";

    const configuredBase = String(process.env.REACT_APP_TENANT_BASE_DOMAIN || "").trim().toLowerCase();
    if (configuredBase && hostname.endsWith(`.${configuredBase}`)) {
        return hostname.slice(0, -(configuredBase.length + 1)).split(".").pop() || "";
    }

    const labels = hostname.split(".");
    if (labels.length < 3) return "";
    const firstLabel = labels[0];
    return ["admin", "api", "app", "apps", "pos", "superadmin", "www"].includes(firstLabel) ? "" : firstLabel;
};

const shouldLogoutForAuthError = (error) => {
    const status = error.response?.status;
    const message = String(error.response?.data?.message || "").toLowerCase();

    if (status === 401) return true;

    if (status !== 403) return false;

    return [
        "invalid or expired token",
        "user account inactive",
        "customer account is not active",
        "assigned branch was not found",
        "assigned branch is inactive",
    ].some((text) => message.includes(text));
};

// Interceptor: automatically attach JWT token to every request
axiosInstance.interceptors.request.use((config) => {
    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    const tenantSubdomain = getTenantSubdomain();
    if (tenantSubdomain) {
        config.headers["x-tenant-subdomain"] = tenantSubdomain;
    }
    // Guard against sending literal 'null'/'undefined' strings
    if (token && token !== 'null' && token !== 'undefined') {
        config.headers.Authorization = `Bearer ${token}`;
    } else {
        delete config.headers.Authorization;
    }
    return config;
});

export default axiosInstance;

// Auto-logout on 401/403 (token expired/invalid)
axiosInstance.interceptors.response.use(
    (response) => {
        emitNetworkStatus(NETWORK_STATUS.ONLINE);
        return response;
    },
    (error) => {
        if (!navigator.onLine) {
            emitNetworkStatus(NETWORK_STATUS.OFFLINE);
        } else if (!error.response) {
            emitNetworkStatus(NETWORK_STATUS.SERVER_DOWN);
        }

        if (shouldLogoutForAuthError(error)) {
            const path = window.location.pathname;
            if (!isLoginRoute(path)) {
                emitSessionExpired(error);
            }
        }
        return Promise.reject(error);
    }
);
